import * as THREE from 'three';

import {
    at,
    ball,
    Backdrop,
    box,
    cloud,
    createKit,
    cyl,
    flat,
    glow,
    GROUND_Y,
    makeWing,
    matte as toon,
    plain as inked,
    rng,
    skyDome,
    starField,
    addClouds,
} from './common';
import { modelGroup } from './models';

const Y = GROUND_Y;
/** The crop beds stand this far above the sand trails. */
const BED_TOP = 0.0125;

/** Is (x) over a planting bed? Used to keep grass tufts and flowers out of the rows. */
const NIGHT = new THREE.Color(0x6f7db4);
const signTexture = (text: string, day: boolean): THREE.CanvasTexture => {
    const c = document.createElement('canvas');
    c.width = 1024;
    c.height = 256;
    const g = c.getContext('2d');
    if (g) {
        g.fillStyle = day ? '#c98a4b' : '#a06a38';
        g.fillRect(0, 0, 1024, 256);
        g.strokeStyle = 'rgba(80,45,15,.45)';
        g.lineWidth = 6;
        [64, 128, 192].forEach((y) => {
            g.beginPath();
            g.moveTo(0, y);
            g.lineTo(1024, y);
            g.stroke();
        });
        g.strokeStyle = '#6b3d17';
        g.lineWidth = 16;
        g.strokeRect(8, 8, 1008, 240);
        // A little tomato
        g.fillStyle = '#e63a2a';
        g.beginPath();
        g.arc(128, 142, 62, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = '#3fae45';
        g.beginPath();
        g.moveTo(128, 84);
        for (let i = 0; i < 5; i++) {
            const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
            g.lineTo(128 + Math.cos(a) * 34, 90 + Math.sin(a) * 18);
            g.lineTo(128 + Math.cos(a + 0.6) * 12, 90 + Math.sin(a + 0.6) * 6);
        }
        g.fill();
        g.font = '900 108px "Baloo 2", Impact, sans-serif';
        g.textAlign = 'center';
        g.textBaseline = 'middle';
        g.lineWidth = 14;
        g.strokeStyle = '#5a1d12';
        g.lineJoin = 'round';
        g.strokeText(text, 600, 134);
        g.fillStyle = '#fff4d6';
        g.fillText(text, 600, 134);
    }
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    return tex;
};

const barn = (day: boolean): THREE.Group => {
    const g = new THREE.Group();
    const red = toon(day ? 0xd7263d : 0x8f1d2c);
    const dark = toon(day ? 0x8a1f2c : 0x5a1620);
    const white = toon(day ? 0xffffff : 0xcfd6e8);
    const glass = day ? toon(0x2a2a3a) : flat(0xffd86b);

    const body = inked(box(red, 7, 4.4, 5.6), 0.025);
    body.position.y = 2.2;
    g.add(body);

    // Roof slabs lie on the gable's slope. Their top faces meet exactly on the ridge (x = 0); the inner end
    // of each slab overlaps its twin underneath, so no gap shows at the peak.
    const halfW = 3.5;
    const wallH = 4.4;
    const gableH = 2.3;
    const slabT = 0.35;
    const overhang = 0.4;
    const pitch = Math.atan2(gableH, halfW);
    const slopeDir = new THREE.Vector2(Math.cos(pitch), -Math.sin(pitch));
    const normal = new THREE.Vector2(Math.sin(pitch), Math.cos(pitch));
    const ridgeTop = new THREE.Vector2(0, wallH + gableH).addScaledVector(
        normal,
        slabT
    );
    const eaveTop = new THREE.Vector2(halfW, wallH).addScaledVector(
        normal,
        slabT
    );
    const slabLen = eaveTop.clone().sub(ridgeTop).dot(slopeDir) + overhang;
    const slabCenter = ridgeTop
        .clone()
        .addScaledVector(slopeDir, slabLen / 2)
        .addScaledVector(normal, -slabT / 2);
    [-1, 1].forEach((side) => {
        const slab = inked(box(dark, slabLen, slabT, 6.1), 0.04);
        slab.position.set(side * slabCenter.x, slabCenter.y, 0);
        slab.rotation.z = -side * pitch;
        g.add(slab);
    });
    const gable = new THREE.Shape();
    gable.moveTo(-halfW, 0);
    gable.lineTo(halfW, 0);
    gable.lineTo(0, gableH);
    gable.closePath();
    [2.82, -2.82].forEach((z) => {
        const tri = new THREE.Mesh(new THREE.ShapeGeometry(gable), red);
        tri.position.set(0, 4.4, z);
        tri.rotation.y = z > 0 ? 0 : Math.PI;
        g.add(tri);
    });

    // Doors with the white X
    g.add(at(box(white, 2.7, 3.2, 0.12), 0, 1.6, 2.83));
    g.add(at(box(dark, 2.35, 2.85, 0.14), 0, 1.58, 2.86));
    [0.78, -0.78].forEach((a) => {
        const bar = box(white, 3.3, 0.16, 0.12);
        bar.position.set(0, 1.58, 2.95);
        bar.rotation.z = a;
        g.add(bar);
    });
    // Hayloft window
    g.add(at(box(white, 1.3, 1.1, 0.12), 0, 5.0, 2.84));
    g.add(at(box(glass, 1.0, 0.8, 0.14), 0, 5.0, 2.87));
    // Corner boards
    [-3.45, 3.45].forEach((x) =>
        g.add(at(box(white, 0.18, 4.4, 0.16), x, 2.2, 2.82))
    );
    // Side windows
    [-1.4, 1.4].forEach((z) =>
        [-3.52, 3.52].forEach((x) => {
            g.add(at(box(glass, 0.06, 0.9, 0.9), x, 2.6, z));
        })
    );
    return g;
};

/** Sand trail, crossroads, fences and tomato bushes, all from instanced primitives. */
const buildCrops = (
    root: THREE.Object3D,
    day: boolean,
    r: () => number,
    keepClear: { x: number; z: number; r: number }
) => {
    const shade = (hex: number) => {
        const c = new THREE.Color(hex);
        return day ? c : c.lerp(NIGHT, 0.6).multiplyScalar(0.7);
    };
    const TRAIL = 2.2; // half-width of the sand trail
    const FENCE = TRAIL + 0.35;
    const BED = BED_TOP;
    const GROUND_LOW = Y - 0.5;
    const Z_FAR = -80;
    const Z_NEAR = 16;
    const X_FAR = 30;
    const dummy = new THREE.Object3D();
    const instanced = (
        geo: THREE.BufferGeometry,
        color: number,
        count: number
    ) => {
        const m = new THREE.InstancedMesh(
            geo,
            toon(shade(color), { flatShading: true }),
            count
        );
        m.frustumCulled = false;
        root.add(m);
        return m;
    };
    const set = (
        m: THREE.InstancedMesh,
        i: number,
        pos: [number, number, number],
        scl: [number, number, number],
        ry = 0
    ) => {
        dummy.position.set(...pos);
        dummy.rotation.set(0, ry, 0);
        dummy.scale.set(...scl);
        dummy.updateMatrix();
        m.setMatrixAt(i, dummy.matrix);
    };

    // ---- sand: the long trail and the crossing road
    const sand = toon(shade(0xe0c58a));
    const trail = new THREE.Mesh(
        new THREE.PlaneGeometry(TRAIL * 2, Z_NEAR - Z_FAR),
        sand
    );
    trail.rotation.x = -Math.PI / 2;
    trail.position.set(0, Y, (Z_NEAR + Z_FAR) / 2);
    root.add(trail);
    const cross = new THREE.Mesh(
        new THREE.PlaneGeometry(X_FAR * 2 + 6, TRAIL * 2),
        sand
    );
    cross.rotation.x = -Math.PI / 2;
    cross.position.set(0, Y + 0.002, 0);
    root.add(cross);

    // ---- raised crop beds: grass tops with earthy walls, so the sand reads as a sunken trail
    const wall = toon(shade(0x7a5632));
    const top = toon(shade(0x7fcb55));
    const BED_X = 70;
    const BED_H = Y + BED - GROUND_LOW;
    for (let sx = -1; sx <= 1; sx += 2)
        for (let sz = -1; sz <= 1; sz += 2) {
            const zLen = sz > 0 ? Z_NEAR + 24 - TRAIL : -Z_FAR + 120 - TRAIL;
            const bedBox = new THREE.Mesh(
                new THREE.BoxGeometry(BED_X - TRAIL, BED_H, zLen),
                [wall, wall, top, wall, wall, wall]
            );
            bedBox.position.set(
                sx * (TRAIL + (BED_X - TRAIL) / 2),
                GROUND_LOW + BED_H / 2,
                sz * (TRAIL + zLen / 2)
            );
            root.add(bedBox);
        }

    // ---- fences: posts and two rails, open where the roads cross
    const GAP = FENCE;
    const STEP = 2.2;
    const posts: [number, number][] = [];
    const rails: [number, number, number, number][] = []; // x, z, length, yaw
    for (let side = -1; side <= 1; side += 2) {
        for (let z = Z_NEAR; z > Z_FAR; z -= STEP) {
            if (Math.abs(z) >= GAP) posts.push([side * FENCE, z]);
            if (Math.abs(z) >= GAP && Math.abs(z - STEP) >= GAP)
                rails.push([side * FENCE, z - STEP / 2, STEP, 0]);
        }
        for (let zz = -1; zz <= 1; zz += 2) {
            for (let x = FENCE + 1; x < X_FAR + 3; x += STEP) {
                posts.push([side * x, zz * GAP]);
                rails.push([
                    side * (x + STEP / 2),
                    zz * GAP,
                    STEP,
                    Math.PI / 2,
                ]);
            }
        }
    }
    const postMesh = instanced(
        new THREE.BoxGeometry(1, 1, 1),
        0x8a5a2b,
        posts.length
    );
    posts.forEach(([x, z], i) =>
        set(postMesh, i, [x, Y + BED + 0.65, z], [0.24, 1.3, 0.24])
    );
    const railMesh = instanced(
        new THREE.BoxGeometry(1, 1, 1),
        0xa8743a,
        rails.length * 2
    );
    rails.forEach(([x, z, len, ry], i) =>
        [0.5, 0.95].forEach((h, k) =>
            set(railMesh, i * 2 + k, [x, Y + BED + h, z], [0.12, 0.14, len], ry)
        )
    );

    // ---- tomato bushes in every quadrant
    const bushes: [number, number][] = [];
    for (let sx = -1; sx <= 1; sx += 2)
        for (let sz = -1; sz <= 1; sz += 2) {
            const far = sz > 0 ? Z_NEAR : -Z_FAR;
            for (let x = FENCE + 1.9; x < X_FAR; x += 2.5)
                for (let d = GAP + 1.6; d < far; d += 1.8) {
                    const bx = sx * (x + (r() - 0.5) * 0.4);
                    const bz = sz * (d + (r() - 0.5) * 0.4);
                    if (
                        Math.hypot(bx - keepClear.x, bz - keepClear.z) <
                        keepClear.r
                    )
                        continue;
                    bushes.push([bx, bz]);
                }
        }
    const bushMesh = instanced(
        new THREE.IcosahedronGeometry(1, 1),
        0xffffff,
        bushes.length
    );
    const tomatoMesh = instanced(
        new THREE.IcosahedronGeometry(1, 1),
        0xe3342a,
        bushes.length * 3
    );
    const green = new THREE.Color();
    bushes.forEach(([x, z], i) => {
        const s = 0.7 + r() * 0.25;
        set(
            bushMesh,
            i,
            [x, Y + BED + 0.55 * s, z],
            [s, s * 0.85, s],
            r() * 6.28
        );
        green.setHSL(0.3 + r() * 0.03, 0.55, 0.3 + r() * 0.12);
        if (!day) green.lerp(NIGHT, 0.6).multiplyScalar(0.7);
        bushMesh.setColorAt(i, green);
        for (let k = 0; k < 3; k++) {
            const a = r() * 6.28;
            const up = 0.15 + r() * 0.7;
            const rad = Math.sqrt(1 - up * up) * s * 0.95;
            set(
                tomatoMesh,
                i * 3 + k,
                [
                    x + Math.cos(a) * rad,
                    Y + BED + 0.55 * s + up * s * 0.85,
                    z + Math.sin(a) * rad,
                ],
                [0.15, 0.15, 0.15]
            );
        }
    });
    (bushMesh.material as THREE.MeshStandardMaterial).color.set(0xffffff);
};

/** A bird that looks like one: body, head, beak, tail and two-jointed feathered flapping wings. */
const makeBird = (tone: number) => {
    const g = new THREE.Group();
    const body = toon(tone, { flatShading: true });
    const belly = toon(
        new THREE.Color(tone).lerp(new THREE.Color(0xffffff), 0.45)
    );
    const beakMat = toon(0xe8a53a);
    g.add(ball(body, 0.17, 0.15, 0.46));
    g.add(at(ball(belly, 0.14, 0.1, 0.34), 0, -0.07, 0.04));
    g.add(at(ball(body, 0.12, 0.12, 0.13), 0, 0.07, 0.46));
    const beak = at(cyl(beakMat, 0.035, 0.16), 0, 0.05, 0.62);
    beak.rotation.x = Math.PI / 2;
    g.add(beak);
    const tail = at(box(body, 0.2, 0.025, 0.34), 0, 0, -0.62);
    tail.rotation.x = -0.12;
    g.add(tail);
    const featherMat = toon(0xffffff, {
        vertexColors: true,
        side: THREE.DoubleSide,
    });
    const base = new THREE.Color(tone);
    const palette = {
        cover: base.clone().lerp(new THREE.Color(0xffffff), 0.15),
        flight: base,
        tip: base.clone().multiplyScalar(0.55),
    };
    const wings = [-1, 1].map((side) => {
        const wing = makeWing(side, palette, featherMat);
        wing.shoulder.position.set(side * 0.1, 0.05, 0.1);
        g.add(wing.shoulder);
        return wing;
    });
    return { g, wings };
};

export const buildFarm = (dark: boolean, signText: string): Backdrop => {
    const day = !dark;
    const kit = createKit({
        sky: day ? 0xe4f6ff : 0x7080c0,
        ground: day ? 0x7ab55a : 0x1b2c3c,
        hemi: day ? 1.5 : 1.0,
        sun: day ? 0xfff3d6 : 0x9fb4ff,
        sunIntensity: day ? 2.2 : 1.2,
    });
    const { scene, root, ticks } = kit;
    const r = rng(11);
    let alive = true;

    scene.add(
        day
            ? skyDome(0x4aa3ee, 0x9ccff6, 0xf6dcdc)
            : skyDome(0x050722, 0x18286a, 0x4a5fa8)
    );

    scene.fog = new THREE.Fog(day ? 0xf1e2df : 0x4a5fa8, 40, day ? 230 : 200);

    // ---- the ground
    const ground = new THREE.Mesh(
        new THREE.PlaneGeometry(400, 434),
        toon(day ? 0x86d65a : 0x2f7a45)
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(0, Y - 0.5, -203);
    root.add(ground);
    // ---- the farm itself
    const b = barn(day);
    b.position.set(-17.5, Y, -26);
    b.rotation.y = 0.22;
    root.add(b);
    const SILO = 4.2;
    const siloGroup = new THREE.Group();
    siloGroup.position.set(-9.2, Y + 0.62 * SILO, -29);
    root.add(siloGroup);
    void modelGroup('silo').then((m) => {
        if (!m || !alive) return;
        m.scale.setScalar(SILO);
        if (!day)
            m.traverse((o) => {
                const mat = (o as THREE.Mesh).material as
                    THREE.MeshStandardMaterial | undefined;
                mat?.color?.copy(NIGHT);
            });
        siloGroup.add(m);
    });

    // The windmill: a modelled tower with separately spinning sails on its axle
    const WM = 5;
    const wmGroup = new THREE.Group();
    wmGroup.position.set(12, Y + 0.62 * WM, -50);
    wmGroup.rotation.y = -0.25;
    root.add(wmGroup);
    const blades = new THREE.Group();
    blades.position.set(-0.04 * WM, 0.42 * WM, 0.6 * WM + 0.3);
    wmGroup.add(blades);
    const night = (g: THREE.Object3D) =>
        g.traverse((o) => {
            const mat = (o as THREE.Mesh).material as
                THREE.MeshStandardMaterial | undefined;
            mat?.color?.copy(NIGHT);
        });
    void modelGroup('windmill_tower').then((m) => {
        if (!m || !alive) return;
        m.scale.setScalar(WM);
        if (!day) night(m);
        wmGroup.add(m);
    });
    void modelGroup('windmill_sails').then((m) => {
        if (!m || !alive) return;
        m.scale.setScalar(5.8 * 0.8);
        if (!day) night(m);
        blades.add(m);
    });

    const sc = new THREE.Group();
    sc.position.set(7, Y + BED_TOP + 1.9 * 0.95 * 0.5, -6);
    sc.rotation.y = -0.45;
    root.add(sc);
    void modelGroup('scarecrow').then((model) => {
        if (!model || !alive) return;
        model.scale.setScalar(2.1);
        model.position.y = 0.95 * 2.1 - 1.9 * 0.95 * 0.5;
        if (!day)
            model.traverse((o) => {
                const mat = (o as THREE.Mesh).material as
                    THREE.MeshStandardMaterial | undefined;
                mat?.color?.copy(NIGHT);
            });
        sc.add(model);
    });
    ticks.push((t) => {
        sc.rotation.z = Math.sin(t * 1.3) * 0.035;
    });

    // Sand trail, crossroads, fences and tomato bushes either side
    buildCrops(root, day, r, { x: 7, z: -6, r: 2 });

    // ---- sky things
    const cloudMat = toon(day ? 0xffffff : 0x4a5a99, {
        emissive: day ? 0x000000 : 0x1a2250,
    });
    ticks.push(
        addClouds(root, cloudMat, day ? 12 : 6, 21, {
            x: 220,
            yMin: 8,
            yMax: 22,
            zMin: 120,
            zMax: 220,
        })
    );

    if (day) {
        // A few birds
        const birds: {
            bird: ReturnType<typeof makeBird>;
            a: number;
            cx: number;
            cy: number;
            sp: number;
            fl: number;
        }[] = [];
        const tones = [0x3b3340, 0x5a4a3a, 0x2f3a4a, 0x6a5a4a, 0x40363a];
        for (let i = 0; i < 5; i++) {
            const bird = makeBird(tones[i]);
            bird.g.scale.setScalar(1.5);
            root.add(bird.g);
            birds.push({
                bird,
                a: r() * 6.28,
                cx: -40 + i * 22,
                cy: Y + 7 + r() * 5,
                sp: 0.25 + r() * 0.2,
                fl: 5 + r() * 2,
            });
        }
        ticks.push((t) => {
            birds.forEach((b2, i) => {
                const a = b2.a + t * b2.sp;
                b2.bird.g.position.set(
                    b2.cx + Math.cos(a) * 14,
                    b2.cy + Math.sin(a * 2) * 1.2,
                    -70 + Math.sin(a) * 10
                );
                // Face along the direction of travel, banking into the turn
                b2.bird.g.rotation.set(
                    0,
                    Math.atan2(-Math.sin(a) * 14, Math.cos(a) * 10),
                    -0.25,
                    'YXZ'
                );
                // Flap in bursts, then glide
                const burst = Math.sin(t * 0.6 + i * 2) > -0.3 ? 1 : 0.12;
                const f = Math.sin(t * b2.fl + i) * burst;
                b2.bird.wings.forEach(({ shoulder, tip, side }) => {
                    shoulder.rotation.z = side * f * 0.7;
                    tip.rotation.z =
                        side * Math.sin(t * b2.fl + i - 0.9) * burst * 0.5;
                });
            });
        });
    } else {
        const moon = ball(flat(0xf6f1d6), 13);
        moon.position.set(-85, 32, -330);
        root.add(moon);
        const halo = glow('rgba(200,215,255,1)', 120, 0.8);
        halo.position.copy(moon.position);
        root.add(halo);
        [
            [-3, 3, 2.2],
            [4, -2, 1.6],
            [0, -5, 1.2],
        ].forEach(([x, y, rad]) => {
            const crater = ball(flat(0xdcd5b8), rad, rad, 0.2);
            crater.position.set(
                moon.position.x + x,
                moon.position.y + y,
                moon.position.z + 12.5
            );
            root.add(crater);
        });
        scene.add(starField(500, 380, 4, { upperOnly: true }));

        // Fireflies
        const n = 90;
        const pos = new Float32Array(n * 3);
        const base: [number, number, number, number][] = [];
        for (let i = 0; i < n; i++) {
            base.push([
                (r() * 2 - 1) * 16,
                Y + 0.5 + r() * 2.2,
                6 - r() * 40,
                r() * 6.28,
            ]);
        }
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
        const flies = new THREE.Points(
            geo,
            new THREE.PointsMaterial({
                color: 0xe6ff6a,
                size: 7,
                sizeAttenuation: false,
                map: undefined,
                transparent: true,
                blending: THREE.AdditiveBlending,
                depthWrite: false,
                toneMapped: false,
            })
        );
        flies.frustumCulled = false;
        root.add(flies);
        ticks.push((t) => {
            base.forEach(([x, y, z, ph], i) => {
                pos[i * 3] = x + Math.sin(t * 0.5 + ph) * 1.2;
                pos[i * 3 + 1] = y + Math.sin(t * 0.8 + ph * 2) * 0.5;
                pos[i * 3 + 2] = z + Math.cos(t * 0.4 + ph) * 1.2;
            });
            geo.attributes.position.needsUpdate = true;
            (flies.material as THREE.PointsMaterial).opacity =
                0.65 + Math.sin(t * 3) * 0.3;
        });
    }

    ticks.push((t) => {
        blades.rotation.z = t * 0.7;
    });
    // Keep the unused cloud helper referenced for tree-shaking friendliness.
    void cloud;
    const done = kit.finish(false);
    const dispose = done.dispose;
    done.dispose = () => {
        alive = false;
        dispose();
    };
    return done;
};
